import React, { useState, useEffect } from "react";
import { Row, Button, Modal, Form, notification } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import axios from "axios";

import { URLS } from "../../constants";
import { useModal } from "../../hooks";
import { TagManufacturerForm } from "../../components/Forms";
import { ContentInner, StyledTable } from "../../pages/styled";

export default function TagManufacturers({ token, environment }) {
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [newTagManufacturer, setNewTagManufacturer] = useState({});
  const [currentTagManufacturer, setCurrentTagManufacturer] = useState({});
  const [itemToDelete, setItemToDelete] = useState(null);

  const initialValues = { name: "", description: "" };
  const [isModalShown, setModalShown, onModalOkAction, onModalCancelAction] =
    useModal();
  const [
    isEditModalShown,
    setEditModalShown,
    onEditModalOkAction,
    onEditModalCancelAction,
  ] = useModal();
  const [
    isDeleteModalShown,
    setDeleteModalShown,
    onDeleteModalOkAction,
    onDeleteModalCancelAction,
  ] = useModal();

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 250,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      align: "center",
      with: 250,
    },
    {
      title: "",
      dataIndex: "edit",
      key: "edit",
      width: 72,
      render: (_, row) => {
        return (
          <Button
            type="link"
            size="small"
            onClick={() => {
              onEditTagManufacturer(row);
            }}
          >
            Edit
          </Button>
        );
      },
    },
    {
      title: "",
      dataIndex: "delete",
      key: "delete",
      width: 88,
      render: (_, row) => {
        return (
          <Button
            danger
            type="link"
            size="small"
            onClick={() => {
              setItemToDelete(row);
              setDeleteModalShown()();
            }}
          >
            Delete
          </Button>
        );
      },
    },
  ];

  const getTagManufacturers = async () => {
    axios
      .get(URLS.FETCH_TAG_MANUFACTURERS, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          env: environment,
        },
      })
      .then((res) => {
        setData(res.data);
      });
  };

  const deleteTagManufacturerById = async () => {
    return axios
      .delete(`${URLS.FETCH_TAG_MANUFACTURERS}/${itemToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          env: environment,
        },
      })
      .then((res) => res.data);
  };

  const onDelete = async () => {
    try {
      const response = await deleteTagManufacturerById();

      if (response) {
        const newData = data.filter((d) => {
          return d.id !== itemToDelete.id;
        });

        setData(newData);
      }
    } catch (e) {
      const message = e?.response?.data?.message || e?.message
      notification.error({
        message: 'Request error',
        description: message
      });
    }
  };

  const handleOk = () => {
    onDelete(itemToDelete);
    onDeleteModalOkAction()();
  };

  const onEditTagManufacturer = (data) => {
    setCurrentTagManufacturer({ ...data });
    setEditModalShown()();
  };

  const updateTagManufacturer = (name, value, isNew) => {
    if (isNew) {
      setNewTagManufacturer({ ...newTagManufacturer, [name]: value });
    } else {
      setCurrentTagManufacturer({ ...currentTagManufacturer, [name]: value });
    }
  };

  const onInputChange = (isNew) => (e) => {
    const { name, value } = e.target;
    updateTagManufacturer(name, value, isNew);
  };

  const resetCurrentTagManufacturer = () => {
    setCurrentTagManufacturer(initialValues);
  };

  const showCreateModal = () => {
    resetCurrentTagManufacturer();
    setModalShown()();
  };

  const createTagManufacturer = async () => (
    axios
    .post(URLS.FETCH_TAG_MANUFACTURERS, newTagManufacturer, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        env: environment,
      },
    })
    .then((res) => res.data)
  );

  const editTagManufacturer = async () => (
    axios
    .put(`${URLS.FETCH_TAG_MANUFACTURERS}/${currentTagManufacturer.id}`, currentTagManufacturer, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        env: environment,
      },
    })
    .then((res) => res.data)
  );

  const onFinishAdd = async () => {
    try {
      onModalOkAction()();
      const response = await createTagManufacturer();

      if (response) {
        setNewTagManufacturer({});
        setData([response, ...data])
      }
    } catch (e) {
      const message = e?.response?.data?.message || e?.message
      notification.error({
        message: 'Request error',
        description: message
      });
    }
  };

  const onFinishEdit = async () => {
    try {
      onEditModalOkAction()();
      const response = await editTagManufacturer();
      if (response) {
        const newData = data.map((item) => {
          if (response.id === item.id) {
            return {...response};
          }
          return item;
        });
        setData(newData);
      }
    } catch (e) {
      const message = e?.response?.data?.message || e?.message
      notification.error({
        message: 'Request error',
        description: message
      });
    }
  };

  useEffect(() => {
    if (!data.length) {
      getTagManufacturers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length]);

  return (
    <>
      <Modal
        title="Delete tag manufacturer"
        visible={isDeleteModalShown}
        onOk={handleOk}
        onCancel={onDeleteModalCancelAction()}
      >
        Are you sure you want to delete tag manufacturer?
      </Modal>
      <Modal
        title="Create new tag manufacturer"
        okText="Create tag mnufacturer"
        visible={isModalShown}
        onOk={() => {}}
        onCancel={() => {
          onModalCancelAction()();
        }}
        okButtonProps={{
          form: "tag-manufacturer-create-form",
          key: "submit",
          htmlType: "submit",
        }}
      >
        <TagManufacturerForm
          form={form}
          onFinish={onFinishAdd}
          onInputChange={onInputChange(true)}
        />
      </Modal>

      <Modal
        title="Edit tag manufacturer"
        okText="Save changes"
        visible={isEditModalShown}
        onOk={() => {}}
        onCancel={() => {
          onEditModalCancelAction()();
          setCurrentTagManufacturer({});
        }}
        okButtonProps={{
          form: "tag-manufacturer-edit-form",
          key: "submit",
          htmlType: "submit",
        }}
      >
        <TagManufacturerForm
          tagManufacturer={currentTagManufacturer}
          isEdit={true}
          form={form}
          onFinish={onFinishEdit}
          onInputChange={onInputChange()}
        />
      </Modal>
      <ContentInner>
        <Button
          type="default"
          icon={<PlusOutlined />}
          onClick={() => {
            showCreateModal();
          }}
          style={{ marginBottom: "25px" }}
        >
          Add new tag manufacturer
        </Button>
        <Row justify="center" width="100%">
          <StyledTable
            style={{ width: "100%" }}
            columns={columns}
            rowKey="id"
            dataSource={data}
            pagination={false}
            tableLayout="fixed"
          />
        </Row>
      </ContentInner>
    </>
  );
}
